import { useEffect, useState } from "react";
import { Megaphone, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { Dialog } from "@/components/modals";
import { api, del, errorMessage, post } from "@/lib/api";
import "./CourseAnnounceDialog.css";

export type AnnounceTarget = {
  courseId: string;
  title: string;
};

type AnnouncementRow = {
  id: string;
  body: string;
  authorName: string | null;
  at: string;
};

type EmailResult = { sent: number; failed: number; skipped: number; at: string };

const BODY_MAX = 2000;

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Compose a class announcement. Students enrolled in this school copy see it
 * on Today. Catalog templates cannot be posted to — the API refuses.
 */
export default function CourseAnnounceDialog({
  target,
  onClose,
}: {
  target: AnnounceTarget | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useNotification();
  const [body, setBody] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [posts, setPosts] = useState<AnnouncementRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) {
      setBody("");
      setSendEmail(true);
      setPosts(null);
      setError("");
      return;
    }
    let cancelled = false;
    api<{ success: boolean; data: AnnouncementRow[] }>(
      `/api/courses/${encodeURIComponent(target.courseId)}/announcements`,
    )
      .then((env) => {
        if (!cancelled) setPosts(env.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const submit = async () => {
    if (!target || busy || !body.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{
        success: boolean;
        data: AnnouncementRow & { email?: boolean; emailResult?: EmailResult | null };
      }>(`/api/courses/${encodeURIComponent(target.courseId)}/announcements`, {
        body: body.trim(),
        email: sendEmail,
      });
      setPosts((prev) => [env.data, ...(prev || [])]);
      setBody("");
      const result = env.data?.emailResult;
      if (!env.data?.email) {
        toast.success("Posted to your class");
      } else if (!result) {
        toast.success("Posted to your class. Email could not be sent.");
      } else if (result.sent === 0 && result.failed === 0) {
        toast.success("Posted to your class. No one in this class has announcement email turned on.");
      } else {
        const sent = `Emailed ${result.sent} ${result.sent === 1 ? "person" : "people"}`;
        const failed = result.failed ? `, ${result.failed} could not be emailed` : "";
        toast.success(`Posted to your class. ${sent}${failed}.`);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!target || busy) return;
    setBusy(true);
    setError("");
    try {
      await del(`/api/courses/${encodeURIComponent(target.courseId)}/announcements/${encodeURIComponent(id)}`);
      setPosts((prev) => (prev || []).filter((row) => row.id !== id));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

  return (
    <Dialog
      open={Boolean(target)}
      title="Announce to your class"
      subtitle={target ? target.title : undefined}
      onClose={onClose}
      width="28rem"
      footer={
        <>
          <button type="button" className="al-announce-btn al-announce-btn--ghost" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="al-announce-btn"
            disabled={busy || !body.trim()}
            onClick={() => void submit()}
          >
            {busy ? "Posting…" : "Post announcement"}
          </button>
        </>
      }
    >
      <textarea
        className="al-announce-input"
        value={body}
        maxLength={BODY_MAX}
        rows={4}
        autoFocus={Boolean(target)}
        placeholder="e.g. Quiz on variables this Friday — finish Practice 1.2 by Thursday."
        onChange={(event) => setBody(event.target.value)}
      />
      <div className="al-announce-meta">
        <span>{displayName ? `Posted as ${displayName}` : "Posted under your name"}</span>
        <span>
          {body.length}/{BODY_MAX}
        </span>
      </div>
      <label className="al-announce-email">
        <input
          type="checkbox"
          checked={sendEmail}
          disabled={busy}
          onChange={(event) => setSendEmail(event.target.checked)}
        />
        <span>
          <span className="al-announce-email-title">Also send by email</span>
          <span className="al-announce-email-sub">
            Teachers on this class are emailed. Students are included only when their class enrollment allows it.
          </span>
        </span>
      </label>
      {error ? (
        <p className="al-announce-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="al-announce-list">
        <div className="al-announce-list-head">
          <Megaphone size={14} aria-hidden />
          Recent posts
        </div>
        {posts == null ? (
          <p className="al-announce-muted">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="al-announce-muted">Nothing posted yet.</p>
        ) : (
          posts.map((row) => (
            <div key={row.id} className="al-announce-row">
              <div>
                <p>{row.body}</p>
                <span>
                  {row.authorName || "Teacher"} · {whenLabel(row.at)}
                </span>
              </div>
              <button
                type="button"
                className="al-announce-delete"
                aria-label="Delete announcement"
                disabled={busy}
                onClick={() => void remove(row.id)}
              >
                <Trash2 size={14} aria-hidden />
              </button>
            </div>
          ))
        )}
      </div>
    </Dialog>
  );
}
